(() => {
  if (customElements.get('shipping-region')) return;
  const KEY = 'bgb:ship-region';
  let picked = null; // explicit pick on this page; wins over everything, even when storage fails
  let geoP;
  let uid = 0;
  const wide = window.matchMedia('(min-width: 769px)'); // popover under the sentence; below this the list is a bottom sheet
  const read = () => { try { return localStorage.getItem(KEY); } catch (e) { return null; } };
  const canPersist = () => { try { return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() !== false; } catch (e) { return true; } };
  const save = (v) => { try { localStorage.setItem(KEY, v); } catch (e) { /* private mode */ } };
  // Shopify's own first-party region, e.g. "CAON" -> "CA-ON"; "" and "CA" give null. Never stored.
  const region = () => {
    try {
      return Promise.resolve(Shopify.customerPrivacy.getRegion()).then((r) => (r && r.length >= 3 ? r.slice(0, 2) + '-' + r.slice(2) : null), () => null);
    } catch (e) { return Promise.resolve(null); }
  };
  const geo = () => geoP || (geoP = new Promise((res) => {
    try {
      if (window.Shopify?.customerPrivacy?.getRegion) res(region());
      else if (window.Shopify?.loadFeatures) Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => res(!err && Shopify.customerPrivacy?.getRegion ? region() : null));
      else res(null);
    } catch (e) { res(null); }
  }));

  class ShippingRegion extends HTMLElement {
    connectedCallback() {
      this.pop = this.querySelector('.fsn-region__pop');
      this.radios = [...this.querySelectorAll('.fsn-region__radio')];
      if (!this.pop || !this.radios.length) return;
      if (!this._bound) {
        this._bound = true;
        // the drawer and the cart page can both be on the page: each list needs its own radio group
        const name = `fsn-region-${(uid += 1)}`;
        this.radios.forEach((r) => { r.name = name; });
        this.addEventListener('click', (e) => {
          if (e.target.closest('.fsn-region__btn')) { if (this.pop.open) this.close(true); else this.open(); }
          else if (e.target.closest('.fsn-region__close')) this.close(true);
          else if (e.target === this.pop) this.close(true); // tap on the sheet's backdrop
          // a pointer pick closes the list; arrow keys only move the choice (their synthetic clicks have detail 0)
          else if (e.target.closest('.fsn-region__opt') && e.detail > 0) setTimeout(() => this.close(true)); // after the label has checked its radio
        });
        this.pop.addEventListener('change', (e) => { if (e.target.matches('.fsn-region__radio')) this.choose(e.target.value); });
        this.pop.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { e.preventDefault(); this.escClose(); }
          // Enter never submits anything (the radios belong to no form); it confirms and closes, like a select
          else if (e.key === 'Enter' && e.target.matches('.fsn-region__radio')) { e.preventDefault(); this.close(true); }
        });
        this.pop.addEventListener('keyup', (e) => { if (e.key === ' ' && e.target.matches('.fsn-region__radio')) this.close(true); });
        this.pop.addEventListener('cancel', (e) => { e.preventDefault(); this.escClose(); });
        // the Escape keyup that follows must not reach the side drawer, which closes itself on it (focus is on our button by then)
        this.addEventListener('keyup', (e) => { if (e.key === 'Escape' && this.escUp) { this.escUp = false; e.stopPropagation(); } });
        this.pop.addEventListener('close', () => this.sync(false));
        // popover only: tabbing or clicking away closes it without pulling focus back
        this.pop.addEventListener('focusout', (e) => { if (!this.pop.matches(':modal') && e.relatedTarget && !this.pop.contains(e.relatedTarget)) this.close(false); });
        this.outside = (e) => { if (!this.contains(e.target)) this.close(false); };
        this.away = () => this.close(false); // resize
        // a scroll closes it only when it could move the button: the page, or a scroller around us (the drawer body). Tickers and other scrollers elsewhere, and the list itself, are ignored.
        this.scrolled = (e) => { const t = e.target; if (t === document || t === window || (t.contains && t.contains(this))) this.close(false); };
      }
      const saved = read();
      const serverPick = this.current();
      if (this.has(picked)) this.apply(picked, false);
      else if (this.has(saved)) this.apply(saved, false);
      else if (serverPick) this.apply(serverPick, false);
      else geo().then((r) => { if (this.isConnected && picked === null && !this.has(read()) && this.has(r)) this.apply(r, true); });
    }
    disconnectedCallback() { this.unlisten(); }
    unlisten() { document.removeEventListener('pointerdown', this.outside); document.removeEventListener('scroll', this.scrolled, true); window.removeEventListener('resize', this.away); }
    escClose() { this.escUp = true; setTimeout(() => { this.escUp = false; }, 1000); this.close(true); }
    has(code) { return !!code && this.radios.some((r) => r.value === code); }
    current() { const r = this.radios.find((x) => x.checked); return r ? r.value : ''; }
    button() { return this.querySelector('.fsn-region__tier.is-active .fsn-region__btn'); }
    open() {
      if (this.pop.open) return;
      if (wide.matches) {
        this.pop.classList.add('is-pop');
        this.pop.show();
        // top layer: escapes the drawer's overflow:auto; without the Popover API (Safari before 17) the CSS keeps it absolutely positioned
        if (this.pop.showPopover) { this.pop.setAttribute('popover', 'manual'); this.pop.showPopover(); this.place(); }
        document.addEventListener('pointerdown', this.outside);
        document.addEventListener('scroll', this.scrolled, true);
        window.addEventListener('resize', this.away);
      }
      else { this.pop.classList.remove('is-pop'); this.pop.showModal(); }
      this.sync(true);
      const list = this.pop.querySelector('.fsn-region__list');
      const on = this.radios.find((r) => r.checked) || this.radios[0];
      const row = on.closest('.fsn-region__opt');
      list.scrollTop = row.offsetTop - list.clientHeight / 2 + row.offsetHeight / 2; // open on the current province
      on.focus({ preventScroll: true });
    }
    // fixed position from the button: centred under the sentence, flipped above when the room below is short, list height capped to the room
    place() {
      const s = this.pop.style;
      const list = this.pop.querySelector('.fsn-region__list');
      const r = this.getBoundingClientRect();
      const b = (this.button() || this).getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const w = Math.min(360, vw - 16);
      s.width = `${w}px`;
      s.left = `${Math.max(8, Math.min(r.left + r.width / 2 - w / 2, vw - w - 8))}px`;
      list.style.maxHeight = '';
      const head = this.pop.offsetHeight - list.offsetHeight;
      const below = vh - b.bottom - 12;
      const above = b.top - 12;
      const up = below < this.pop.offsetHeight && above > below;
      list.style.maxHeight = `${Math.max(120, Math.min(308, (up ? above : below) - head))}px`;
      s.top = up ? `${Math.max(8, b.top - 4 - this.pop.offsetHeight)}px` : `${b.bottom + 4}px`;
    }
    close(refocus) {
      this.unlisten();
      if (this.pop.open) this.pop.close();
      if (this.pop.hasAttribute('popover')) { try { this.pop.hidePopover(); } catch (e) { /* already hidden */ } this.pop.removeAttribute('popover'); this.pop.removeAttribute('style'); this.pop.querySelector('.fsn-region__list').style.maxHeight = ''; }
      this.sync(false);
      if (refocus) this.button()?.focus();
    }
    sync(open) { this.querySelectorAll('.fsn-region__btn').forEach((b) => b.setAttribute('aria-expanded', open ? 'true' : 'false')); }
    choose(code) {
      picked = code;
      if (canPersist()) save(picked);
      document.querySelectorAll('shipping-region').forEach((el) => el.apply && el.apply(picked, false));
      const live = this.querySelector('[data-live]');
      const t = this.querySelector('.fsn-region__tier.is-active');
      const r = this.radios.find((x) => x.checked);
      if (live && t && r) live.textContent = `${t.dataset.announce || ''} ${r.closest('.fsn-region__opt').querySelector('.fsn-region__nm').textContent}`.trim();
    }
    apply(code, fromGeo) {
      if (!this.radios || !this.has(code)) return;
      const r = this.radios.find((x) => x.value === code);
      r.checked = true;
      const tier = r.dataset.tier || 'default';
      const nm = r.closest('.fsn-region__opt').querySelector('.fsn-region__nm').textContent;
      this.querySelectorAll('.fsn-region__val').forEach((v) => { v.textContent = nm; });
      const wasFocused = this.button() === document.activeElement;
      this.querySelectorAll('[data-tier-panel]').forEach((p) => p.classList.toggle('is-active', p.dataset.tierPanel === tier));
      if (wasFocused) this.button()?.focus(); // the old tier's button just went invisible
      this.querySelector('.fsn-region__note')?.classList.toggle('is-off', !fromGeo);
      this.dispatchEvent(new CustomEvent('shipping-region:apply', { bubbles: true, detail: { tier } }));
    }
  }
  customElements.define('shipping-region', ShippingRegion);
})();
